import { postData } from '../utils/ajax-utils';
import { toggleClassWhenSticky } from '../utils/toggle-class-when-sticky';
import Modal, { ModalHTMLElement } from '../components/modal';
import ripplet from 'ripplet.js';
import scrollManager from '../utils/scroll-manager';
import { SlimSelectHTMLElement } from '../utils/slim-select';
import { Option } from 'slim-select/dist/data';
import { uniqid } from '../utils/utils';

export default function initArchiveMediatheque() {
    const $archiveMediatheque = document.querySelector<HTMLElement>('.js-page-mediatheque-archive');

    if ($archiveMediatheque) {
        new ArchiveMediathequePage($archiveMediatheque);
    }
}

export class ArchiveMediathequePage {
    $container: HTMLElement;
    $btnsAddToSelection: NodeListOf<HTMLElement>;
    $popupAddToSelectionSuccess: HTMLElement | null;
    $popupRemoveFromSelectionSuccess: HTMLElement | null;
    $buttonYourSelection;
    addToSelectionController: AbortController | null = null;
    getResultsAbortController: AbortController | null = null;
    removeFromSelectionController: AbortController | null = null;
    $modalSelection;
    $modalSelectionContainer;
    $modalCgu;
    $modalCguCheckbox;
    $modalCguSubmit;
    $searchForm: HTMLFormElement | null;
    $searchFormInput: HTMLInputElement | null;
    $searchFormContainer: HTMLElement | null;
    $resultsContainer: HTMLElement;
    $resetFiltersBtn: HTMLButtonElement | null;
    resultsCache: {[key: string]: string} = {};

    constructor($container: HTMLElement) {
        this.$container = $container;
        this.$btnsAddToSelection = document.querySelectorAll<ModalHTMLElement>('[data-modal-id] [data-add-to-selection]');
        this.$popupAddToSelectionSuccess = document.querySelector<HTMLElement>('.popup-add-to-selection');
        this.$popupRemoveFromSelectionSuccess = document.querySelector<HTMLElement>('.popup-remove-from-selection');
        this.$modalSelection = document.querySelector<ModalHTMLElement>('[data-modal-id="mediatheque_selection"]');
        this.$modalSelectionContainer = document.querySelector<ModalHTMLElement>('[data-modal-id="mediatheque_selection"] .modal-container');
        this.$modalCgu = document.querySelector<ModalHTMLElement>('[data-modal-id="mediatheque_cgu"]');
        this.$modalCguCheckbox = document.querySelector<ModalHTMLElement>('[data-modal-id="mediatheque_cgu"] #mediatheque_accept_cgu');
        this.$modalCguSubmit = document.querySelector<ModalHTMLElement>('[data-modal-id="mediatheque_cgu"] #mediatheque_download_button');
        this.$buttonYourSelection = document.querySelector<HTMLButtonElement>('[data-modal-open="mediatheque_selection"]');
        this.$searchForm = this.$container.querySelector('.js-mediatheque-search-form');
        this.$searchFormInput = this.$container.querySelector('.js-mediatheque-search-form input[name="s"]');
        this.$searchFormContainer = this.$container.querySelector('.mediatheque-search-documents');
        this.$resultsContainer = this.$container.querySelector('.js-mediatheque-archive-results') as HTMLElement;
        this.$resetFiltersBtn = this.$container.querySelector<HTMLButtonElement>('.js-mediatheque-search-form-reset-filters-button');

        // Mise en cache des résultats affichés au chargement de la page
        this.resultsCache[location.href] = document.documentElement.outerHTML;

        // Charger le contenu correspondant lorsque l'utilisateur tente de naviguer
        // dans son historique via les pushState fait préalablement
        addEventListener('popstate', () => {
            this.displayAjaxResults(document.location.href, false);
        });

        // Envoi d'un event GTM lors de la sélection d'un filtre
        this.$container.querySelectorAll<SlimSelectHTMLElement>('select.js-slimselect').forEach($select => {
            $select.slim.beforeOnChange = (newValues: Option|Option[]) => {
                const oldValues = $select.slim.selected();
                let newValue = newValues as Option|null;

                if (Array.isArray(newValues)) {
                    newValue = null;
                    newValues.forEach(nv => {
                        if (!oldValues.includes(nv.value || '')) {
                            newValue = nv;
                            return;
                        }
                    });
                }

                if (newValue && oldValues !== newValue.value) {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                        'event': 'media_filter_interaction',
                        'filter_type': $select.slim.config.placeholderText,
                        'filter_selected': newValue.text
                    });
                }
            };

            // S'il y a déjà une méthode `onChange`, on l'enregistre pour l'override
            const fn = $select.slim.onChange?.bind({});
            $select.slim.onChange = info => {
                // Appel de l'ancien onChange()
                if (fn) {
                    fn(info);
                }

                // On masque le bouton de reset des filtres si aucun filtre
                // n'est sélectionné (et on l'affiche dans le cas contraire)
                this.$resetFiltersBtn?.classList.toggle('d-none', !this.isFiltersSelected());
            };
        });

        // On masque le bouton de reset des filtres si aucun filtre
        // n'est sélectionné (et on l'affiche dans le cas contraire)
        this.$resetFiltersBtn?.classList.toggle('d-none', !this.isFiltersSelected());

        // Activation de l'ajax lors de la navigation via la pagination
        document.addEventListener('click', evt => {
            const $target = evt.target as HTMLElement;

            if ($target.dataset.addToSelection) {
                this.addToSelection($target);
            }

            if ($target.closest('.navigation-pagination') && $target instanceof HTMLAnchorElement) {
                evt.preventDefault();

                this.displayAjaxResults($target.href);
            }
        });
        
        this.$modalCguCheckbox?.addEventListener('change', (evt: Event) => {
            const $target = evt.target as HTMLInputElement;
            if ($target.checked) {
                this.$modalCguSubmit?.removeAttribute('disabled');
            } else {
                this.$modalCguSubmit?.setAttribute('disabled', 'disabled');
            }
        });

        document.addEventListener('click', (evt: Event) => {
            const $target = evt.target as HTMLElement;
            if ($target.classList.contains('media-trash-button')) {
                this.removeFromSelection($target);
            }
        });

        // Le bouton "Valider" de la sélection ferme la modal de sélection et ouvre celle des cgu
        document.addEventListener('click', (evt: Event) => {
            if ((<HTMLElement>evt.target).id === 'mediatheque_selection_submit_button') {
                this.$modalSelection?.close();
                this.$modalCgu?.open();
            }
            return false;
        });


        // L'image de la card media est cliquable
        document.addEventListener('click', (evt: Event) => {
            // Si c'est un élément enfant de article.media
            if ((<HTMLElement>evt.target).closest('article.media') && (<HTMLElement>evt.target).closest('.image-wrapper')) {
                // Le bouton d'ouverture de la modal médiathèque est-il présent ?
                const $mediaDownloadButton = (<HTMLElement>(<HTMLElement>evt.target).closest('article.media')?.querySelector('.media-download-button'));
                if ($mediaDownloadButton) {
                    $mediaDownloadButton.click();
                }
            }
        });

        this.$modalCguSubmit?.addEventListener('click', () => {
            // Envoi d'un event GTM lors du téléchargement des medias
            if (this.$modalSelection) {
                const $selectionMedias = this.$modalSelection.querySelectorAll<HTMLElement>('.media[data-media-id]');
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'purchase',
                    ecommerce: {
                        currency: 'EUR',
                        value: 1.00,
                        transaction_id: uniqid(),
                        items: Array.from($selectionMedias).map(($el: HTMLElement) => {
                            return {
                                item_name: $el.dataset.mediaTitle,
                                item_id: $el.dataset.mediaId,
                                quantity: 1,
                            };
                        })
                    }
                });
            }

            // Diffère le rechargement
            setTimeout(() => {
                location.reload();
            }, 1000);
        });

        // Gestion des filtres de recherche sticky
        if (this.$searchFormContainer) {
            toggleClassWhenSticky(this.$container, 'is-sticky-filters', this.$searchFormContainer);
            toggleClassWhenSticky(this.$container, 'sticky-filters', document.body);
        }

        // Formulaire de recherche
        // A la soumission du champ de recherche, on soumet le formulaire
        if (this.$searchForm instanceof HTMLFormElement) {
            this.$searchForm.addEventListener('keypress', (evt: KeyboardEvent) => {
                const $target = evt.target as HTMLElement;
                if ($target === this.$searchFormInput && evt.key === 'Enter') {
                    this.$searchForm?.submit();
                }
            });

            this.$searchForm.addEventListener('submit', (evt: SubmitEvent) => {
                evt.preventDefault();
                this.searchFormSubmit();
            });

            // Reset de tous les filtres au click sur le bouton de reset
            this.$searchForm.addEventListener('reset', evt => {
                evt.preventDefault();

                this.$container.querySelectorAll<SlimSelectHTMLElement>('select.js-slimselect').forEach($select => {
                    $select.slim.set($select.slim.slim.multiSelected ? [] : '');
                });
            });
        }
    }

    /**
     * Ajoute un média dans la sélection de l'utilisateur
     * 
     * @param $btnAddToSelection
     */
    addToSelection($btnAddToSelection: HTMLElement) {
        const mediaId = ($btnAddToSelection.getAttribute('data-add-to-selection') ?? '');
        const mediaTitle = ($btnAddToSelection.getAttribute('data-add-to-selection-title') ?? '');
        //close popup before adding selected media
        $btnAddToSelection.closest<ModalHTMLElement>('[data-modal-id]')?.close();
        this.addToSelectionController = new AbortController();
        postData(script_var.ajax_url, {
            action: 'mediatheque_add_media_to_selection',
            media_id: mediaId,
        }, this.addToSelectionController.signal).then(response => {
            //$btnAddToSelection.closest<ModalHTMLElement>('[data-modal-id]')?.close();

            if (this.$modalSelectionContainer) {
                this.$modalSelectionContainer.innerHTML = response.data.modal_mediatheque_selection;
            }
            if (this.$buttonYourSelection) {
                this.$buttonYourSelection.innerHTML = this.$buttonYourSelection.innerHTML.replace(/\(\d+\)/g, '(' +response.data.mediatheque_selection.length+ ')');
            }
            if (this.$popupAddToSelectionSuccess) {
                this.$popupAddToSelectionSuccess.classList.remove('hidden');
                if($btnAddToSelection.dataset.redirect) {
                    window.location.href = $btnAddToSelection.dataset.redirect;
                }
                setTimeout(() => {
                    this.$popupAddToSelectionSuccess?.classList.add('hidden');
                }, 2000);

                // Envoi d'un event GTM lors de l'ajout au panier d'un media
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'add_to_cart',
                    ecommerce: {
                        currency: 'EUR',
                        items: [{
                            item_id: mediaId,
                            item_name: mediaTitle,
                            quantity: 1
                        }]
                    }
                });
            }
        }).catch(() => {
            this.addToSelectionController = null;
        });
            
        return false;
    }

    /**
     * Supprime dans la sélection de l'utilisateur
     * 
     * @param $btnAddToSelection
     */
    removeFromSelection($btnAddToSelection: HTMLElement) {
        const mediaId = ($btnAddToSelection.getAttribute('data-media-id')?? '');
        //remove elt before calling ajax action
        this.$modalSelectionContainer?.querySelector('.media[data-media-id="' + mediaId + '"]')?.remove();
        this.removeFromSelectionController = new AbortController();
        postData(script_var.ajax_url, {
            action: 'mediatheque_remove_media_from_selection',
            media_id: mediaId,
        }, this.removeFromSelectionController.signal).then(response => {
            const nb_items = (response.data.mediatheque_selection.length ?? 0);
            const modal_main_sub = this.$modalSelectionContainer?.querySelector('.modal-main-sub');
            
            if (modal_main_sub) {
                modal_main_sub.innerHTML = nb_items + ' ' + response.data.modal_mediatheque_selection_nb_elements_title;
            }

            if (this.$popupRemoveFromSelectionSuccess) {
                this.$popupRemoveFromSelectionSuccess.classList.remove('hidden');
                setTimeout(() => {
                    this.$popupRemoveFromSelectionSuccess?.classList.add('hidden');
                }, 2000);
            }
            //this.$modalSelectionContainer?.querySelector('.media[data-media-id="' + mediaId + '"]')?.remove();

            if (nb_items === 0) {
                this.$modalSelectionContainer?.querySelector('#mediatheque_selection_submit_button')?.remove();
            }

            if (this.$buttonYourSelection) {
                this.$buttonYourSelection.innerHTML = this.$buttonYourSelection.innerHTML.replace(/\(\d+\)/g, '(' + nb_items + ')');
            }
        }).catch(() => {
            this.removeFromSelectionController = null;
        });
            
        return false;
    }

    searchFormSubmit() {
        if (!(this.$searchForm instanceof HTMLFormElement)) {
            return;
        }

        // Construction de l'url
        const url = new URL((this.$searchForm.getAttribute('action') ?? location.origin + location.pathname));
        (new FormData(this.$searchForm)).forEach((value: FormDataEntryValue, key: string) => {
            if (!(value instanceof File)) {
                url.searchParams.append(key, value);
            }
        });

        this.displayAjaxResults(url.toString());
    }

    /**
     * Affiche les résultats via ajax
     * 
     * @param url
     * @param withPushState
     */
    displayAjaxResults(url: string, withPushState = true) {
        // Annulation de la requête précédente
        if (this.getResultsAbortController) {
            this.getResultsAbortController.abort();
        }

        if (withPushState) {
            // Changement de l'URL
            window.history.pushState(null, '', url);
        }

        if (typeof this.resultsCache[url] !== 'undefined') {
            this.displayResults(url, this.resultsCache[url]);
            return;
        }

        this.getResultsAbortController = new AbortController();

        this.$resultsContainer.classList.add('loading');

        fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            signal: this.getResultsAbortController.signal,
        }).then(response => {
            return response.text();
        }).then(content => {
            this.displayResults(url, content);

            this.$resultsContainer.classList.remove('loading');
        }).catch(() => {
            this.$resultsContainer.classList.remove('loading');
        });
    }

    displayResults(url: string, content: string) {
        // Récupération du HTML contenant les résultats
        const div = document.createElement('div');
        div.innerHTML = content;
        const resultsHTML = (div.querySelector('.js-mediatheque-archive-results') as HTMLElement).innerHTML;

        // Mise en cache du résultat
        if (typeof this.resultsCache[url] === 'undefined') {
            this.resultsCache[url] = content;
        }

        // Ajout des résultats dans le DOM
        this.$resultsContainer.innerHTML = resultsHTML;

        // Initialisation ripplet sur les boutons
        this.$resultsContainer.querySelectorAll<HTMLElement>('.ripplet').forEach($el => $el.addEventListener('click', ripplet));

        // Initialisation modales
        this.$resultsContainer.querySelectorAll<ModalHTMLElement>('[data-modal-id]').forEach($modal => new Modal($modal));

        // Scroll jusqu'à la barre de recherche
        const adminBarHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--admin-bar-height'));
        const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'));
        const scrollOffset = adminBarHeight + headerHeight + 50;
        scrollManager.scrollTo((this.$container as HTMLElement), true, scrollOffset);
    }

    isFiltersSelected(): boolean {
        const nbNotEmpty = Array.from(this.$container.querySelectorAll<SlimSelectHTMLElement>('select.js-slimselect')).filter($select => {
            const selectedData = $select.slim.selected();

            return Array.isArray(selectedData) ? selectedData.length : selectedData;
        }).length;

        return 0 !== nbNotEmpty;
    }
}
